import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AppService } from "@App/app.service";
import { AccountSettingsDTO } from "@DTOs";
import { AccountSettings } from "@Enums";
import { Account, Client, UWResponse } from "@Models";
import { AccountSettingsDataService } from "@Services";
import { SharedModule } from "@Shared/shared.module";
import { TimeUtils, Utils } from "@Utils";
import { catchError, map, of } from "rxjs";
import { SubSink } from "subsink";

export interface TaskToggleSetting {
    accountSettingName: AccountSettings;
    formControlName: string;
    title: string;
    description: string;
    defaultValue: boolean;
    currentValue?: boolean;
    initialValue?: boolean;
    accountSettingId?: string;
}

@Component({
    selector: 'workbenchv2-task-options',
    templateUrl: './workbenchv2-task-options.component.html',
    imports: [SharedModule]
})
export default class WorkbenchV2TaskOptionsComponent implements OnInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;

    accountSettings: AccountSettingsDTO[] = [];
    accountSettingsLoaded = false;
    errorMessage = '';
    lastUpdated: Date;

    autoAssignTasksControlName = 'autoAssignTasksOnAssignment';
    enableTasksControlName = 'enableTasks';

    toggleSettings: TaskToggleSetting[] = [
        {
            accountSettingName: AccountSettings.UWWorkbenchEnableTasks,
            formControlName: this.enableTasksControlName,
            title: 'Enable Tasks',
            description: 'Enable or disable tasks in the workbench.',
            defaultValue: false,
            currentValue: false,
            initialValue: false
        },
        {
            accountSettingName: AccountSettings.UWWorkbenchAutoAssignTasksOnAssignment,
            formControlName: this.autoAssignTasksControlName,
            title: 'Auto Assign Tasks on Assignment',
            description: 'Automatically assign unassigned tasks to the case assignee when a case is assigned. Tasks assigned to the previous assignee are controlled by the Reassign Tasks setting.',
            defaultValue: true,
            currentValue: true,
            initialValue: true
        },
        {
            accountSettingName: AccountSettings.UWWorkbenchReassignTasksOnAssignment,
            formControlName: 'reassignTasksOnAssignment',
            title: 'Reassign open tasks on case reassignment',
            description: 'Automatically transfers all open tasks assigned to the previous case assignee to the new case assignee.',
            defaultValue: false,
            currentValue: false,
            initialValue: false
        },
        {
            accountSettingName: AccountSettings.UWWorkbenchDuplicateTaskNotesInUWNotes,
            formControlName: 'duplicateTaskNotesInUWNotes',
            title: 'Duplicate Task Notes in UW Notes',
            description: 'Automatically duplicate task notes in the underwriter notes.',
            defaultValue: false,
            currentValue: false,
            initialValue: false
        }
    ];

    subs = new SubSink();

    constructor(
        private readonly appService: AppService,
        private readonly accountSettingsDataService: AccountSettingsDataService,
    ) { }

    ngOnInit() {
        this.subscribeToAccountSettings();
    }

    subscribeToAccountSettings() {
        this.subs.add(this.accountSettingsDataService.loadAccountSettings(this.account.id).subscribe(accountSettings => {
            this.accountSettings = accountSettings;
            this.setForm();
            this.accountSettingsLoaded = true;
        }));
    }

    setForm() {
        const settingsDates: Date[] = [];
        this.toggleSettings.forEach(setting => {
            const existingSetting = this.accountSettings.find(x => x.name === setting.accountSettingName);
            const currentValue = existingSetting ? existingSetting.value?.toLowerCase() === 'true' : setting.defaultValue;

            setting.currentValue = currentValue;
            setting.initialValue = currentValue;
            setting.accountSettingId = existingSetting?.id;

            const date = TimeUtils.convertToDateCst(existingSetting?.lastModifiedDate ?? existingSetting?.creationDate);
            if (date) {
                settingsDates.push(date);
            }
        });

        this.lastUpdated = settingsDates.length > 0
            ? settingsDates.sort((a, b) => b.getTime() - a.getTime())[0]
            : null;
    }

    onToggleFeature(toggleSetting: TaskToggleSetting, checked: boolean) {
        toggleSetting.currentValue = checked;
        this.saveToggleSetting(toggleSetting).subscribe();
    }

    saveToggleSetting(toggleSetting: TaskToggleSetting) {
        const updateAccountSetting: AccountSettingsDTO = {
            id: toggleSetting.accountSettingId ?? Utils.emptyGuid,
            accountId: this.account.id,
            name: toggleSetting.accountSettingName,
            value: toggleSetting.currentValue.toString()
        };

        return this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map(() => {
                toggleSetting.initialValue = toggleSetting.currentValue;
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();
                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                toggleSetting.currentValue = toggleSetting.initialValue;
                return of(false);
            }),
        );
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}
